import { jewelInstanceEffectText } from '@kenocyber/game-domain';
import { jewelModeNotice, type OwnedJewel } from '@kenocyber/shared';
import { useEffect, useRef, useState } from 'react';
import { jewelFilterId } from './JewelElectricFilters';
import { jewelLook } from './jewel-appearance';
import { observeJewelCard } from './jewel-motion';

/**
 * Uma carta de joia, com a sua borda animada (gate V28C).
 *
 * ## A arte-base é imutável, e a borda vive noutra camada
 *
 * O `<img>` desenha o derivado e nada mais lhe acontece: não é recolorido,
 * mascarado, escalado por hover nem filtrado. Tudo o que brilha - o aro, o halo,
 * a eletricidade - são pseudo-elementos e camadas irmãs, por cima e por baixo,
 * que podem ser desligadas sem tocar num pixel do PNG.
 *
 * ## Sem canvas, sem WebGL, sem renderer por carta
 *
 * Quinze cartas seriam quinze contextos, e este projeto tem um `Phaser.Game` por
 * aba por regra. A borda é CSS: um gradiente cónico atrás de uma máscara
 * circular para as esferas, e a máscara de silhueta gerada para a soberana.
 *
 * ## As camadas, e o que cada uma faz
 *
 * A. **O halo** (`.jewel-halo`) - largo, saturado, por baixo de tudo. É o que
 *    dá a sensação de luz à distância.
 * B. **O aro** (`.jewel-ring`) - fino, neon e FECHADO em toda a circunferência.
 *    Acompanha a esfera, ou a silhueta real da soberana, nunca o quadrado.
 * C. **Os filamentos** (`.jewel-arc`) - o mesmo aro desenhado através de um
 *    filtro de ruído partilhado, que o torna irregular e vivo. Dois deles: um
 *    contínuo e outro intermitente, que dá os ramos.
 * D. **A luz interior** (`.jewel-inner`) - dentro da joia, recortada pela
 *    máscara alpha da peça, com a assinatura da sua família.
 * E. **A moldura da carta** - deliberadamente mais discreta, na mesma cor mas
 *    com menos intensidade, para não competir com o nome nem com o estado.
 *
 * ## Nada disto é um canvas
 *
 * Nem um. O halo e o interior são gradientes, o aro é um `border`, e os
 * filamentos são um filtro SVG declarado UMA vez para toda a gente. Não há
 * `requestAnimationFrame`, não há `ResizeObserver` por carta e não há textura
 * gerada em runtime - ver `JewelElectricFilters` para o crédito da técnica e
 * para o que foi deliberadamente não copiado dela.
 *
 * ## A cor vem da peça; a família só empresta o movimento
 *
 * As três variáveis de cor são declaradas por joia no catálogo visual. Uma
 * família nunca fornece cor: fornece forma e ritmo. É por isso que o Núcleo
 * Atômico se contorna a laranja sobre grafite e a Chama da Destruição a
 * vermelho, sendo ambas da mesma família de movimento.
 *
 * ## O estado não depende da cor
 *
 * Uma peça encaixada tem um selo com texto e um traço sólido na moldura; uma
 * livre não tem nenhum dos dois. Quem não distingue cores continua a ler o
 * estado - e é por isso que `data-state` existe no DOM em vez de ser só uma
 * classe pintada.
 *
 * ## Os estados, e cada um diz-se por palavras
 *
 * Normal, hover, selecionada, encaixada e indisponível - mais `active`, que é a
 * soberana. `data-state` carrega quatro (`free`, `installed`, `unavailable`,
 * `active`) e a seleção vive num atributo à parte: uma peça selecionada continua
 * a poder ficar indisponível, e um único atributo obrigaria a escolher qual dos
 * dois se esconde. O hover é do rato e não é estado nenhum.
 *
 * ## Indisponível diz PORQUÊ
 *
 * Nunca um cinzento sem explicação: a carta escreve a razão. Uma peça que não
 * pode ser encaixada porque não há assento livre diz isso, e o jogador sabe o
 * que fazer a seguir em vez de ficar a clicar num botão morto.
 */

export interface JewelCardProps {
  readonly jewel: OwnedJewel;
  /** O que a carta diz sobre onde a peça está. Vazio quando está livre. */
  readonly seatLabel?: string | null;
  /** A base das URLs de arte, como o resto do jogo já a resolve. */
  readonly assetBaseUrl: string;
  readonly onAction?: () => void;
  readonly actionLabel?: string | null;
  /** Uma carta pode estar a aguardar o servidor; então não aceita cliques. */
  readonly busy?: boolean;
  /** A peça escolhida para o próximo encaixe. Uma de cada vez. */
  readonly selected?: boolean;
  /**
   * A razão de não poder ser escolhida agora, em palavras, ou `null`.
   *
   * É uma frase e não um booleano de propósito: quem desativa uma carta tem de
   * conseguir dizer porquê, e quem lê o ecrã tem direito a saber.
   */
  readonly unavailable?: string | null;
  readonly onSelect?: (() => void) | undefined;
  readonly children?: React.ReactNode;
}

export function JewelCard({
  jewel,
  seatLabel,
  assetBaseUrl,
  onAction,
  actionLabel,
  busy = false,
  selected = false,
  unavailable = null,
  onSelect,
  children,
}: JewelCardProps) {
  const look = jewelLook(jewel.definitionId);
  const effect = jewelInstanceEffectText(jewel.definitionId, jewel.level);
  /*
   * O aviso de MODO, perguntado ao catálogo (gate V28G.O.1).
   *
   * Nunca uma comparação de ids aqui: a condição é "produz efeito no duelo e não
   * na caça", e quem sabe isso é `supportedModes`. Uma lista escrita neste
   * ficheiro seria uma segunda casa para a mesma decisão, e a segunda joia
   * exclusiva de duelo entraria sem aviso.
   */
  const modeNotice = jewelModeNotice(jewel.definitionId);
  const [visible, setVisible] = useState(true);
  const root = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const element = root.current;
    if (!element) {
      return;
    }
    return observeJewelCard(element, setVisible);
  }, []);

  const installed = jewel.seat !== null;
  /*
   * A máscara alpha da peça, derivada pelo pipeline.
   *
   * Serve duas coisas: o contorno da soberana, que um aro circular cortaria; e
   * a luz DENTRO de qualquer joia, que tem de acabar exatamente onde a arte
   * acaba. Sem ela, um brilho interno escaparia para pixels transparentes -
   * há uma prova que mede isso, pixel a pixel, contra esta mesma máscara.
   *
   * `none` só acontece se um dia uma arte chegar sem máscara; a folha de estilo
   * trata esse caso deixando a camada interior sem recorte, e portanto vazia.
   */
  const maskUrl = jewel.maskFile ? `url("${assetBaseUrl}${jewel.maskFile}")` : 'none';
  /*
   * A soberana não tem assento, e isso não a torna livre.
   *
   * `seat` nulo significa "não está encaixada em lado nenhum", que para as
   * catorze quer dizer LIVRE - podem ir para um socket - e para ela não quer
   * dizer nada: ela nunca ocupa um assento, porque o que se faz com ela é
   * incorporá-la numa nave. Ler o modo em vez do assento é a única forma de a
   * carta não oferecer um gesto que não existe.
   *
   * O nome `active` ficou, e é o nome do TRATAMENTO VISUAL - o halo, o aro e o
   * arco que a arte aprovou para esta peça, e que `data-state="active"` na folha
   * de estilo desenha. Não é uma afirmação sobre efeito: até ao gate V28F.A era
   * também isso, porque a posse concedia; deixou de ser, e a palavra que o
   * jogador lê mudou com o facto (ver `JewelPanel`).
   */
  const active = jewel.socketMode === 'sovereign';
  /*
   * Encaixada ganha a indisponível, e não ao contrário.
   *
   * Uma peça que está numa skin está algures - dizer-lhe "indisponível" seria
   * verdade e inútil ao mesmo tempo, porque esconderia a única informação que
   * interessa: ONDE ela está.
   */
  const state = active ? 'active' : installed ? 'installed' : unavailable ? 'unavailable' : 'free';
  const pickable = onSelect !== undefined && !active && !installed && unavailable === null && !busy;

  return (
    <div
      ref={root}
      className="jewel-card"
      data-testid={`jewel-card-${jewel.instanceId}`}
      data-definition={jewel.definitionId}
      data-family={look.family}
      data-shape={jewel.visualShape}
      data-state={state}
      data-selected={selected ? 'yes' : 'no'}
      data-animating={visible ? 'yes' : 'no'}
      style={
        {
          '--jewel-primary': look.palette.primary,
          '--jewel-secondary': look.palette.secondary,
          '--jewel-core': look.palette.core,
          /* O ciclo BASE. O hover deriva o seu a partir dele, em CSS. */
          '--jewel-cycle': `${look.idleMs}ms`,
          '--jewel-mask': maskUrl,
          /* O filtro elétrico PARTILHADO da família. Um por família, não por
             carta: quinze cartas referem nove filtros. */
          '--jewel-arc-filter': `url(#${jewelFilterId(look.family)})`,
          '--jewel-branch-filter': `url(#${jewelFilterId(look.family, 'branch')})`,
        } as React.CSSProperties
      }
    >
      <div className="jewel-art">
        {/*
         * A energia contínua da borda vive DENTRO de um recorte anular.
         *
         * O halo e o filamento são as duas camadas que percorrem toda a
         * circunferência, e são as duas que, em certas fases do ruído, apareciam
         * como um arco liso afastado da esfera - o experimento de camadas
         * mostrou o arco a sobreviver mesmo com o filamento desligado, ou seja,
         * o halo também o produzia. Fechá-las numa faixa presa à esfera resolve
         * as duas de uma vez, e sem lhes tocar na amplitude nem no brilho.
         *
         * O filtro fica no FILHO e a máscara no PAI: aplicados ao mesmo
         * elemento, o CSS mascararia depois de deslocar e o recorte deixaria de
         * significar o que significa.
         *
         * As faíscas ficam de fora de propósito: são seis pontos soltos, podem
         * escapar, e nunca formam arco. A luz interior, o PNG e a peça soberana
         * também ficam de fora.
         */}
        {active ? null : (
          <span className="jewel-energy-clip" aria-hidden="true">
            <span className="jewel-halo" />
            <span className="jewel-arc" />
          </span>
        )}
        {/* O halo largo e saturado, por baixo de tudo - só na soberana. */}
        {active ? <span className="jewel-halo" aria-hidden="true" /> : null}
        {/*
         * O contorno contínuo existe SÓ para a soberana.
         *
         * Nas catorze esferas era um círculo perfeito por baixo da eletricidade,
         * e por mais que se lhe baixasse a opacidade ou se lhe cortassem
         * janelas, havia sempre uma fase do ruído em que ele aparecia como um
         * grande arco liso ao lado da joia - o defeito reapareceu três vezes,
         * sempre noutro instante do ciclo, e sempre por causa desta camada.
         * Uma esfera não precisa dele: o filamento fragmentado e o halo é que
         * desenham a borda.
         *
         * A soberana continua a levá-lo, e é outra coisa: ali ele é a banda
         * carmesim que segue a silhueta, e nunca um círculo.
         */}
        {active ? <span className="jewel-ring" aria-hidden="true" /> : null}
        {/*
         * Os filamentos elétricos.
         *
         * Dois: um que percorre o contorno inteiro, e outro mais deslocado e
         * intermitente, que dá os ramos que nascem e desaparecem. Ambos são o
         * MESMO aro desenhado com o filtro partilhado da família - o ruído é
         * que os torna irregulares, e é ele que se move.
         */}
        {/* Nas esferas o filamento já vive dentro do recorte, acima. */}
        {active ? <span className="jewel-arc" aria-hidden="true" /> : null}
        <span className="jewel-arc jewel-arc-branch" aria-hidden="true" />
        <img
          className="jewel-image"
          src={`${assetBaseUrl}${jewel.assetFile}`}
          alt={jewel.displayName}
          width={128}
          height={128}
          draggable={false}
        />
        {/*
         * A luz DENTRO da joia, recortada pela máscara alpha da própria peça.
         *
         * Três camadas numa só caixa - o núcleo aqui, a faixa de luz no
         * `::before`, a assinatura da família no `::after` - porque a máscara do
         * pai recorta os filhos, e uma máscara chega para as três. Mistura em
         * `screen`: acende o material sem tapar o símbolo que está desenhado.
         */}
        {/*
         * A ÚNICA camada acima da arte, e o que ela leva lá (revisão visual V28G.K).
         *
         * Levava quatro coisas: um brilho anelar a respirar, uma faixa de luz da
         * cor da joia, a assinatura da família e - por baixo de tudo - a imagem
         * a 45 % de opacidade quando indisponível. Somadas, o cristal preto
         * lia-se castanho e o PNG do inventário não era o PNG do ficheiro.
         *
         * Saíram todas. Ficou UMA: o brilho BRANCO que atravessa a peça de vez
         * em quando, pedido depois de a arte estar limpa. Branco e não da cor da
         * joia, porque um reflexo não tinge o material - revela-o.
         *
         * O resto da energia continua ATRÁS da arte - o halo em `z-index: 0`, os
         * filamentos em 1 - e é por isso que ela existe sem tocar num pixel do
         * derivado.
         *
         * A soberana mantém as quatro: foi aprovada assim.
         */}
        <span className="jewel-inner" aria-hidden="true" />
      </div>
      <div className="jewel-facts">
        <p className="jewel-name">{jewel.displayName}</p>
        <p className="jewel-level">Nível {jewel.level}</p>
        {/*
         * O que a peça FAZ, derivado da mesma constante que o combate usa.
         *
         * Nunca escrito à mão aqui nem em CSS: `jewelInstanceEffectText` lê o
         * coeficiente do catálogo e formata-o, por isso mudar o número muda o
         * que o jogador lê sem que ninguém se lembre.
         *
         * **Ausente para OITO das catorze, e a ausência é uma dívida** (V28G.W.1).
         *
         * Não é "ainda não decidido": as oito têm mecânica registada e provada -
         * ver `damage/jewel-mechanics.test.ts`. O que falta é a REDAÇÃO, porque
         * `jewelInstanceEffectText` lê COEFICIENTES e elas não têm nenhum, e
         * porque o catálogo não tem campo de descrição autoral.
         *
         * A omissão continua certa enquanto não houver texto: um `+0%` diria
         * "medido, e vale zero", que é falso para uma bomba atómica. O defeito
         * não é omitir - é não existir nada para pôr no lugar.
         *
         * `jewel-card-description.test.tsx` fixa as oito por nome, e reprova no
         * dia em que uma delas ganhar frase.
         */}
        {effect === null ? null : (
          <p className="jewel-effect" data-testid={`jewel-effect-${jewel.instanceId}`}>
            {effect}
          </p>
        )}
        {/*
         * **Onde a joia NÃO produz efeito** (gate V28G.O.1).
         *
         * ## Duas metades, e nenhuma delas é uma cor
         *
         * A etiqueta e a frase são texto, e o `<strong>` é a ênfase que o próprio
         * HTML tem. Um daltónico lê exatamente a mesma coisa que toda a gente, e
         * uma prova pode afirmá-la sem inspecionar um pixel - a mesma disciplina
         * que `jewel-state` já segue nesta carta.
         *
         * ## Porque `role="note"` e não `role="alert"`
         *
         * Porque isto não é um erro nem uma recusa. A peça pode ser encaixada, e
         * vai ser encaixada; o que se diz é onde ela vale. Um `alert` interromperia
         * o leitor de ecrã para anunciar um problema que não existe.
         *
         * A frase inteira vive no `aria-label` para a tecnologia assistiva ler as
         * duas metades como uma coisa só, em vez de soletrar a sigla e depois
         * encontrar uma frase solta.
         */}
        {modeNotice === null ? null : (
          <p
            className="jewel-mode-notice"
            data-testid={`jewel-mode-notice-${jewel.instanceId}`}
            role="note"
            aria-label={`${modeNotice.badge}. ${modeNotice.note}`}
          >
            <strong className="jewel-mode-badge">{modeNotice.badge}</strong>
            <span className="jewel-mode-note">{modeNotice.note}</span>
          </p>
        )}
        {/*
         * O estado por PALAVRAS, e não só por cor.
         *
         * "Encaixada" e a skin onde está, ou "Livre". Um daltónico lê a mesma
         * coisa que toda a gente, e um teste pode afirmá-la sem inspecionar
         * pixels.
         */}
        <p className="jewel-state" data-testid={`jewel-state-${jewel.instanceId}`}>
          {active
            ? (seatLabel ?? 'No inventário')
            : installed
              ? (seatLabel ?? 'Encaixada')
              : unavailable
                ? `Indisponível — ${unavailable}`
                : selected
                  ? 'Selecionada'
                  : 'Livre'}
        </p>
      </div>
      {/*
       * A escolha é um botão de verdade.
       *
       * Um `div` com `onClick` não recebe foco, não responde ao teclado e não
       * se anuncia - e escolher uma joia é a única coisa que esta carta faz.
       * Cobre a carta inteira e fica POR BAIXO da ação, para o botão de ação
       * continuar a ser clicável.
       */}
      {onSelect ? (
        <button
          type="button"
          className="jewel-pick"
          data-testid={`jewel-pick-${jewel.instanceId}`}
          onClick={onSelect}
          disabled={!pickable}
          aria-pressed={selected}
          aria-label={`Selecionar ${jewel.displayName}`}
        />
      ) : null}
      {actionLabel && onAction ? (
        <button
          type="button"
          className="jewel-action"
          data-testid={`jewel-action-${jewel.instanceId}`}
          onClick={onAction}
          disabled={busy}
        >
          {actionLabel}
        </button>
      ) : null}
      {children}
    </div>
  );
}
